import { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, PieChart } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SERIES_COLORS } from '../charts/chartTypes';
import { formatPlayers } from '../../utils/starchart';
import type { ModpackShareSlice } from '../../types/starchart';

const SLICE_LIMIT = 6;
const OTHER_COLOR = 'var(--tone-neutral-text)';

interface ModpackShareCardProps {
  distribution: ModpackShareSlice[];
  totalPlayers: number;
  className?: string;
}

/**
 * Where the network's players actually are, by modpack.
 *
 * Drawn as a stacked share bar with a ranked legend rather than a pie. Every function of a pie
 * is kept — proportion, top-N plus a collapsible remainder, click-through to the busiest server —
 * and a bar stays readable at 360px, keeps the labels horizontal, and sorts naturally, which a
 * ring of six similar wedges does not.
 */
export const ModpackShareCard = memo(
  ({ distribution, totalPlayers, className }: ModpackShareCardProps) => {
    const [otherExpanded, setOtherExpanded] = useState(false);

    const { top, other, otherPlayers } = useMemo(() => {
      const head = distribution.slice(0, SLICE_LIMIT);
      const tail = distribution.slice(SLICE_LIMIT);
      return {
        top: head,
        other: tail,
        otherPlayers: tail.reduce((sum, slice) => sum + slice.players, 0),
      };
    }, [distribution]);

    if (distribution.length === 0) {
      return (
        <div className={cn('glass-card p-4 md:p-5', className)}>
          <SectionHeader title="Players by modpack" icon={PieChart} />
          <EmptyState
            bare
            compact
            icon={PieChart}
            title="Nobody online"
            description="No server on the network is currently carrying players."
          />
        </div>
      );
    }

    const busiestPath = (slice: ModpackShareSlice) => `/starchart/${slice.servers[0]?.fqdn ?? ''}`;

    return (
      <div className={cn('glass-card p-4 md:p-5', className)}>
        <SectionHeader
          title="Players by modpack"
          icon={PieChart}
          count={distribution.length}
          description={`${formatPlayers(totalPlayers)} players across ${distribution.length} modpacks`}
        />

        <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-glass-200/10">
          {top.map((slice, i) => (
            <div
              key={slice.key}
              className="h-full"
              style={{
                width: `${slice.share * 100}%`,
                background: SERIES_COLORS[i % SERIES_COLORS.length],
              }}
              title={`${slice.label}: ${formatPlayers(slice.players)} (${(slice.share * 100).toFixed(1)}%)`}
            />
          ))}
          {otherPlayers > 0 && (
            <div
              className="h-full"
              style={{
                width: `${(otherPlayers / Math.max(totalPlayers, 1)) * 100}%`,
                background: OTHER_COLOR,
              }}
              title={`Other: ${formatPlayers(otherPlayers)}`}
            />
          )}
        </div>

        <ul className="mt-4 divide-y divide-glass-200/10">
          {top.map((slice, i) => (
            <li key={slice.key}>
              <Link
                to={busiestPath(slice)}
                className="flex items-center gap-3 py-2.5 hover:bg-glass-200/10 rounded-lg px-2 -mx-2 transition-colors"
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }}
                  aria-hidden="true"
                />
                {slice.thumbnailUrl && (
                  <img
                    src={slice.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    className="w-6 h-6 rounded-md object-cover shrink-0"
                  />
                )}
                <span className="text-sm text-glass-300 truncate flex-1 min-w-0">{slice.label}</span>
                <span className="text-xs text-glass-400 tabular-nums shrink-0">
                  {slice.servers.length} {slice.servers.length === 1 ? 'server' : 'servers'}
                </span>
                <span className="text-sm font-medium text-white tabular-nums shrink-0 w-12 text-right">
                  {formatPlayers(slice.players)}
                </span>
                <span className="text-xs text-glass-400 tabular-nums shrink-0 w-12 text-right">
                  {(slice.share * 100).toFixed(1)}%
                </span>
              </Link>
            </li>
          ))}

          {other.length > 0 && (
            <li>
              <button
                type="button"
                onClick={() => setOtherExpanded((prev) => !prev)}
                className="w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-glass-200/10 transition-colors"
                aria-expanded={otherExpanded}
              >
                {otherExpanded ? (
                  <ChevronDown className="w-4 h-4 text-glass-400 shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-glass-400 shrink-0" />
                )}
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ background: OTHER_COLOR }}
                  aria-hidden="true"
                />
                <span className="text-sm text-glass-300 flex-1 text-left">
                  Other ({other.length} modpacks)
                </span>
                <span className="text-sm font-medium text-white tabular-nums shrink-0 w-12 text-right">
                  {formatPlayers(otherPlayers)}
                </span>
                <span className="text-xs text-glass-400 tabular-nums shrink-0 w-12 text-right">
                  {((otherPlayers / Math.max(totalPlayers, 1)) * 100).toFixed(1)}%
                </span>
              </button>

              {otherExpanded && (
                <ul className="subcard mt-1 mb-2 divide-y divide-glass-200/10">
                  {other.map((slice) => (
                    <li key={slice.key}>
                      <Link
                        to={busiestPath(slice)}
                        className="flex items-center gap-3 py-2 px-3 hover:bg-glass-200/10 transition-colors"
                      >
                        <span className="text-sm text-glass-300 truncate flex-1 min-w-0">
                          {slice.label}
                        </span>
                        <span className="text-sm font-medium text-white tabular-nums shrink-0">
                          {formatPlayers(slice.players)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
      </div>
    );
  },
);

ModpackShareCard.displayName = 'ModpackShareCard';
